import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getPublicScreening,
  startPublicScreening,
  savePublicAnswer,
  completePublicScreening,
  apiErrorMessage,
} from "../api/screeningApi";
import "../css/Screening.css";

// Browser speech recognition (Chrome / Edge). In production this is the part
// you would swap for a voice platform like Vapi.
const SpeechRecognition = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const speechSupported = !!SpeechRecognition && typeof window !== "undefined" && "speechSynthesis" in window;

// Browsers don't say whether a voice is male or female, so we recognise them by
// name. These cover the common voices in Chrome, Edge (Windows) and Safari (Mac).
const FEMALE_VOICE_NAMES = [
  "female", "neerja", "heera", "kalpana", "swara", "veena", "lekha", "zira", "aria", "jenny", "michelle",
  "ana", "emma", "ava", "hazel", "susan", "libby", "sonia", "maisie", "samantha", "victoria", "karen",
  "moira", "tessa", "fiona", "allison", "serena", "google us english",
];
const MALE_VOICE_NAMES = [
  "male", "prabhat", "ravi", "hemant", "rishi", "david", "mark", "guy", "christopher", "eric", "roger",
  "steffan", "andrew", "brian", "george", "ryan", "thomas", "alfie", "elliot", "oliver", "alex", "daniel",
  "fred", "aaron", "arthur",
];

const voiceGender = (voice) => {
  const name = voice.name.toLowerCase();
  // check "female" names first, because "female" also contains "male"
  if (FEMALE_VOICE_NAMES.some((n) => name.includes(n))) return "female";
  if (MALE_VOICE_NAMES.some((n) => name.includes(n))) return "male";
  return null;
};

// Picks the best voice for the accent + gender. Prefers the exact accent
// (en-IN), then any English voice of that gender, then any voice of the accent.
// Natural/online voices (e.g. Edge "Neerja Online (Natural)") sound best, so they win ties.
function pickVoice(voices, lang, gender) {
  const english = voices.filter((v) => v.lang && v.lang.toLowerCase().startsWith("en"));
  const sameLang = english.filter((v) => v.lang.replace("_", "-").toLowerCase() === lang.toLowerCase());
  const byQuality = (list) =>
    [...list].sort((a, b) => Number(/natural|online|google/i.test(b.name)) - Number(/natural|online|google/i.test(a.name)));

  const exact = byQuality(sameLang.filter((v) => voiceGender(v) === gender));
  if (exact.length) return { voice: exact[0], matched: true };

  const anyEnglish = byQuality(english.filter((v) => voiceGender(v) === gender));
  if (anyEnglish.length) return { voice: anyEnglish[0], matched: true };

  const fallback = byQuality(sameLang)[0] || byQuality(english)[0] || null;
  return { voice: fallback, matched: false };
}

export default function ScreeningInterview() {
  const { token } = useParams();

  // loading | unavailable | welcome | interview | done
  const [phase, setPhase] = useState("loading");
  const [fatalMessage, setFatalMessage] = useState("");
  const [info, setInfo] = useState(null);

  const [lang, setLang] = useState("en-IN");
  const [gender, setGender] = useState("female"); // interviewer voice
  const [voices, setVoices] = useState([]);
  const [consent, setConsent] = useState(false);
  const [startError, setStartError] = useState("");
  const [starting, setStarting] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [log, setLog] = useState([]); // finished Q&A, shown in the collapsible "previous answers" panel
  const [finalText, setFinalText] = useState("");
  const [interimText, setInterimText] = useState("");
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(true); // true while the AI is speaking or saving
  const [answerError, setAnswerError] = useState("");

  // Refs hold values the speech callbacks need without stale closures.
  const recognitionRef = useRef(null);
  const listeningRef = useRef(false);
  const finalRef = useRef("");
  const interimRef = useRef("");
  const answerStartRef = useRef(0);
  const utteranceRef = useRef(null); // keeps Chrome firing onend reliably
  const liveBoxRef = useRef(null); // the live answer box, auto-scrolled as the candidate speaks
  const queueRef = useRef([]);
  const finishedRef = useRef(false);

  // ---------- Load the browser's voices (they arrive in the background) ----------
  useEffect(() => {
    if (!speechSupported) return;
    const load = () => setVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.addEventListener("voiceschanged", load);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", load);
  }, []);

  const { voice: chosenVoice, matched: voiceMatched } = pickVoice(voices, lang, gender);

  // Keep the newest words visible inside the live answer box as it fills up.
  useEffect(() => {
    const box = liveBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [finalText, interimText]);

  // Each new question starts at the top of the page.
  useEffect(() => {
    if (phase === "interview") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentIndex, phase]);

  // ---------- Load the interview ----------
  useEffect(() => {
    if (!speechSupported) {
      setFatalMessage("Your browser doesn't support voice interviews. Open this link in Google Chrome or Microsoft Edge on a laptop or desktop.");
      setPhase("unavailable");
      return;
    }
    (async () => {
      try {
        const { data } = await getPublicScreening(token);
        setInfo(data.interview);
        if (data.interview.speechLanguage) setLang(data.interview.speechLanguage);
        queueRef.current = data.interview.questions.filter((q) => !data.interview.answeredQids.includes(q.qid));
        setPhase("welcome");
      } catch (err) {
        setFatalMessage(apiErrorMessage(err, "Couldn't load your interview. Check your connection and refresh the page."));
        setPhase("unavailable");
      }
    })();
  }, [token]);

  // Warn before closing the tab mid-interview; stop audio when leaving the page.
  useEffect(() => {
    const warn = (e) => {
      if (phase === "interview" && !finishedRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase]);

  useEffect(() => {
    return () => {
      listeningRef.current = false;
      try { recognitionRef.current?.abort(); } catch { /* not running */ }
      if (speechSupported) window.speechSynthesis.cancel();
    };
  }, []);

  // ---------- Voice helpers ----------
  const speak = (text) =>
    new Promise((resolve) => {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      if (chosenVoice) {
        utterance.voice = chosenVoice;
        utterance.lang = chosenVoice.lang;
      }
      // If no male voice is installed, a slightly lower pitch is the closest we can get.
      utterance.pitch = !voiceMatched && gender === "male" ? 0.8 : 1;
      utterance.rate = 0.95;
      utterance.onend = resolve;
      utterance.onerror = resolve;
      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    });

  const setupRecognition = (language) => {
    const recognition = new SpeechRecognition();
    recognition.lang = language;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalRef.current += transcript.trim() + " ";
        else interim += transcript;
      }
      interimRef.current = interim;
      setFinalText(finalRef.current);
      setInterimText(interim);
      if ((finalRef.current + interim).trim()) setAnswerError("");
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        listeningRef.current = false;
        setListening(false);
        setAnswerError("Microphone access is blocked. Allow it from the address bar, then press Repeat question.");
      } else if (event.error === "network") {
        setAnswerError("Speech recognition lost its connection. Check your internet, then press Repeat question.");
      }
      // "no-speech" and "aborted" are normal pauses; onend restarts recognition.
    };

    // Chrome stops listening after a pause — restart while the candidate is still answering.
    recognition.onend = () => {
      if (listeningRef.current) {
        try { recognition.start(); } catch { /* already restarting */ }
      } else {
        setListening(false);
      }
    };

    recognitionRef.current = recognition;
  };

  const startListening = () => {
    listeningRef.current = true;
    if (!answerStartRef.current) answerStartRef.current = Date.now();
    try { recognitionRef.current.start(); } catch { /* already started */ }
    setListening(true);
  };

  const stopListening = () => {
    listeningRef.current = false;
    try { recognitionRef.current?.stop(); } catch { /* not running */ }
    setListening(false);
  };

  // ---------- Flow ----------
  const askQuestion = async (index) => {
    const q = queueRef.current[index];
    setCurrentIndex(index);
    finalRef.current = "";
    interimRef.current = "";
    answerStartRef.current = 0;
    setFinalText("");
    setInterimText("");
    setAnswerError("");
    setBusy(true);
    await speak(q.text);
    startListening();
    setBusy(false);
  };

  const handleStart = async () => {
    if (!consent) {
      setStartError("Tick the checkbox to confirm before starting.");
      return;
    }
    setStarting(true);
    setStartError("");
    try {
      // Ask for the microphone up front so the permission prompt doesn't interrupt the first answer.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      setStartError("Microphone access was blocked. Allow the microphone from your browser's address bar, then press Start interview again.");
      setStarting(false);
      return;
    }

    try {
      await startPublicScreening(token, { consent: true, speechLanguage: lang });
    } catch (err) {
      setStartError(apiErrorMessage(err, "Couldn't start the interview. Try again."));
      setStarting(false);
      return;
    }

    setupRecognition(lang);
    setPhase("interview");
    setStarting(false);

    if (queueRef.current.length === 0) {
      await finish();
      return;
    }
    setBusy(true);
    await speak(
      `Hello ${info.candidateFirstName}. Thank you for joining. I'll ask you ${queueRef.current.length} questions. ` +
        `Take your time with each answer, and press Submit answer when you're done. Let's begin.`
    );
    askQuestion(0);
  };

  const handleRepeat = async () => {
    stopListening();
    setBusy(true);
    await speak(queueRef.current[currentIndex].text);
    startListening();
    setBusy(false);
  };

  const submitAnswer = async (skipped) => {
    const answer = skipped ? "" : `${finalRef.current} ${interimRef.current}`.trim();
    if (!skipped && !answer) {
      setAnswerError("We didn't catch an answer yet. Speak your answer, or press Skip.");
      return;
    }
    stopListening();
    setBusy(true);
    setAnswerError("");

    const q = queueRef.current[currentIndex];
    const durationSec = answerStartRef.current ? (Date.now() - answerStartRef.current) / 1000 : 0;

    try {
      await savePublicAnswer(token, { qid: q.qid, answer, durationSec, skipped });
    } catch (err) {
      setAnswerError(apiErrorMessage(err, "Your answer wasn't saved. Press Submit answer again."));
      setBusy(false);
      return;
    }

    setLog((prev) => [...prev, { question: q.text, answer, skipped }]);

    const next = currentIndex + 1;
    if (next < queueRef.current.length) {
      await speak(skipped ? "Okay, next question." : "Thank you.");
      askQuestion(next);
    } else {
      await finish();
    }
  };

  const finish = async () => {
    stopListening();
    setBusy(true);
    try {
      await completePublicScreening(token);
    } catch (err) {
      setAnswerError(apiErrorMessage(err, "Couldn't finish the interview. Check your connection and try again."));
      setBusy(false);
      return;
    }
    finishedRef.current = true;
    await speak("That was the last question. Thank you for your time. The recruitment team will be in touch soon.");
    setPhase("done");
  };

  // ---------- Render ----------
  const total = info?.questions.length || 0;
  const alreadyAnswered = total - queueRef.current.length;
  const questionNumber = alreadyAnswered + currentIndex + 1;
  const current = queueRef.current[currentIndex];

  return (
    <div className="scr-public">
      <div className="scr-public-inner">
        <div className="scr-brand">HirePeaks Consulting</div>

        {phase === "loading" && <p className="scr-lead">Loading your interview…</p>}

        {phase === "unavailable" && (
          <div className="scr-card">
            <h2>Interview unavailable</h2>
            <p>{fatalMessage}</p>
          </div>
        )}

        {phase === "welcome" && info && (
          <>
            <h1>Hi {info.candidateFirstName}</h1>
            <p className="scr-lead">
              This is your screening interview for {info.jobTitle}. It has {total} questions and takes about 15 minutes.
            </p>
            <div className="scr-card">
              <h2>Before you start</h2>
              <p>
                You'll speak with an AI interviewer. It reads each question aloud, and your spoken answer appears on screen
                as text while you talk. When you've finished an answer, press <strong>Submit answer</strong>.
              </p>
              <p style={{ color: "var(--color-text-secondary)" }}>
                Sit in a quiet room and allow microphone access when your browser asks. Your answers are saved as text and
                reviewed by the recruitment team.
              </p>

              <label htmlFor="scr-lang">Your English accent (helps the speech recognition)</label>
              <select id="scr-lang" value={lang} onChange={(e) => setLang(e.target.value)}>
                <option value="en-IN">English (India)</option>
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
              </select>

              <label htmlFor="scr-voice">Interviewer voice</label>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <select id="scr-voice" value={gender} onChange={(e) => setGender(e.target.value)}>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => speak(`Hello ${info.candidateFirstName}, I'll be your interviewer today.`)}
                >
                  Preview voice
                </button>
              </div>
              <p style={{ color: "var(--color-text-secondary)", fontSize: "12px", margin: "6px 0 0" }}>
                {!chosenVoice
                  ? "Your browser's default voice will be used."
                  : voiceMatched
                  ? `Voice: ${chosenVoice.name}`
                  : `A ${gender} voice isn't installed on this computer, so ${chosenVoice.name} will be used.`}
              </p>

              <label className="scr-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                    setStartError("");
                  }}
                />
                I understand I'm speaking with an AI interviewer and agree to my answers being saved and reviewed.
              </label>

              {startError && <div className="scr-error">{startError}</div>}
              <div className="scr-actions">
                <button type="button" onClick={handleStart} disabled={starting}>
                  {starting ? "Starting…" : "Start interview"}
                </button>
              </div>
            </div>
          </>
        )}

        {phase === "interview" && info && (
          <>
            {/* Stays fixed at the top of the screen while the page scrolls */}
            <div className="scr-progress-header">
              <div className="scr-progress-row">
                <span>{current ? `Question ${questionNumber} of ${total}` : "Finishing…"}</span>
                <span className="scr-mic">
                  <span className={`scr-dot ${listening ? "on" : ""}`} />
                  {listening ? "Listening…" : busy ? "Interviewer speaking…" : "Microphone off"}
                </span>
              </div>
              <div className="scr-progress">
                <div style={{ width: `${Math.round(((questionNumber - 1) / Math.max(total, 1)) * 100)}%` }} />
              </div>
            </div>

            {current && (
              <div className="scr-current">
                <div className="scr-bubble">
                  <span className="scr-who">AI interviewer</span>
                  {current.text}
                </div>
                <div className="scr-bubble me live">
                  <span className="scr-who">Your answer (live)</span>
                  <div className="scr-live-box" ref={liveBoxRef} aria-live="polite">
                    {finalText || interimText ? (
                      <>
                        {finalText} <span className="scr-interim">{interimText}</span>
                      </>
                    ) : (
                      <span className="scr-interim">
                        {listening ? "Start speaking. Your words will appear here." : "Listen to the question…"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="scr-action-bar">
              {answerError && <div className="scr-error">{answerError}</div>}
              <div className="scr-actions" style={{ marginTop: 0 }}>
                <button type="button" onClick={() => submitAnswer(false)} disabled={busy}>Submit answer</button>
                <button type="button" className="btn-secondary" onClick={handleRepeat} disabled={busy}>Repeat question</button>
                <button type="button" className="btn-secondary" onClick={() => submitAnswer(true)} disabled={busy}>Skip</button>
              </div>
            </div>

            {log.length > 0 && (
              <details className="scr-history">
                <summary>Your previous answers ({log.length})</summary>
                <div className="scr-history-list">
                  {log.map((item, i) => (
                    <div className="scr-history-item" key={i}>
                      <div className="scr-history-q">{item.question}</div>
                      <div>{item.skipped ? <em>Skipped</em> : item.answer}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}

        {phase === "done" && (
          <div className="scr-card">
            <h2>Thank you, your interview is complete</h2>
            <p>Your answers have been saved. The recruitment team will review them and get back to you.</p>
          </div>
        )}
      </div>
    </div>
  );
}
